import React, { useState, useEffect } from 'react';
import '../App.css';
import { Link, useNavigate } from 'react-router-dom';
import { baseUrl } from '../components/BaseUrl';
import LoadingOverlay from 'react-loading-overlay-ts';
import axios from 'axios';
import Swal from "sweetalert2";
import Quiz from 'react-quiz-component';
import '../lib/quiz/style.css';

const QuizContainer = ({ quizData, courseId }) => {
  const navigate = useNavigate();
  const [isActive, setActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(parseFloat(localStorage.getItem("duration")) * 60);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [timerId, setTimerId] = useState(null);
  const [quizResult, setQuizResultState] = useState(null); // Store quiz result

  const startQuiz = () => {
    setQuizStarted(true);
    
    // Start the timer
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    
    setTimerId(timer);
  };

  const stopTimer = () => {
    if (timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  };

  useEffect(() => {
    // Clean up timer on unmount
    return () => {
      stopTimer();
    };
  }, []);

  useEffect(() => {
    if (timeLeft <= 0 || quizCompleted) {
      stopTimer();
    }
  }, [timeLeft, quizCompleted]);

  const handleTimeUp = () => {
    Swal.fire({
      title: "Time's Up!",
      text: "Your time for the quiz has ended.",
      icon: "warning",
      confirmButtonText: "OK",
      allowEscapeKey: false,
      allowOutsideClick: false,
      backdrop: `
        rgba(143, 70, 0, 0.3)
        url("")
        left top
        no-repeat
      `
    }).then((result) => {
      if (result.isConfirmed) {
        // If we have stored quiz result, use it, otherwise submit with current progress
        if (quizResult) {
          submitScore(quizResult);
        } else {
          // If no result is available (user didn't complete any questions), submit with zero score
          submitScore({ correctPoints: 0, totalPoints: quizData.questions?.length || 0 });
        }
      }
    });
  };

  const onQuizComplete = (result) => {
    console.log("Quiz completed with result:", result);
    setQuizResultState(result); // Store the result
    setQuizCompleted(true);
    stopTimer();
    submitScore(result);
  };

  const submitScore = (result) => {
    // Prevent multiple submissions
    if (quizCompleted) return;
    
    setQuizCompleted(true);
    
    const token = localStorage.getItem("codefussion-login-token");
    setActive(true);
    
    const userData = {
      first_name: localStorage.getItem("codefussion-student-first-name"),
      last_name: localStorage.getItem("codefussion-student-last-name"),
      email: localStorage.getItem("codefussion-student-email"),
      course: courseId.toLowerCase(),
      total_point: result.totalPoints || quizData.questions?.length || 0,
      current_score: result.correctPoints || 0
    };
    
    // console.log("Submitting score:", userData);
    
    axios.post(`${baseUrl}update-score.php`, userData, { 
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token
      }
    }).then((response) => {
      setActive(false);
      console.log("Server response:", response);
      if(response.data.message === "Score updated."){
        Swal.fire({
          title: "Success",
          text: "Score updated successfully",
          icon: "success",
          confirmButtonText: "OK",
          backdrop: `
          rgba(143, 70, 0, 0.3)
          url("")
          left top
          no-repeat
        `
        }).then((result) => {
        if (result.isConfirmed) {
            window.location.replace("/dashboard")
        }
        });
      } else {
        Swal.fire({
          title: "Error",
          text: "Error updating score. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
          backdrop: `
          rgba(143, 70, 0, 0.3)
          url("")
          left top
          no-repeat
        `
        });
      }
    }).catch((error) => {
      setActive(false);
      console.error("Error submitting score:", error);
      Swal.fire({
        title: "Error",
        text: "Error updating score. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `
        rgba(143, 70, 0, 0.3)
        url("")
        left top
        no-repeat
      `
      });
    });
  };

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="quiz-container">
      {/* Header */}
      <div className="p-4 mb-4 border-bottom bg-white shadow-sm">
        <div className="d-flex justify-content-between align-items-center">
          <Link to="/quiz" className="text-decoration-none">
            <i className="fas fa-arrow-left text-primary me-2"></i>
            <span className="text-primary">Back to Quizzes</span>
          </Link>
          {quizStarted && (
            <div className="text-center">
              <div className="fs-4 fw-bold text-primary bg-light px-4 py-2 rounded-pill border">
                <i className="fas fa-clock me-2"></i>
                Time Left: {formatTime()}
              </div>
            </div>
          )}
          <div className="text-end">
            <Link to="/quiz">
              <i className="fas fa-times text-danger fs-4"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container">
        {!quizStarted ? (
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              {/* Welcome Card */}
              <div className="card border-0 shadow-lg mb-5">
                <div className="card-header bg-primary text-white py-4 border-0">
                  <div className="text-center">
                    <i className="fas fa-graduation-cap fa-3x mb-3"></i>
                    <h1 className="h2 fw-bold mb-2 text-light">Assessment Ready</h1>
                    <p className="mb-0 opacity-75">Get ready to demonstrate your knowledge</p>
                  </div>
                </div>
                
                <div className="card-body p-5">
                  {/* Assessment Overview */}
                  <div className="row mb-5">
                    <div className="col-md-8">
                      <h1 className="text-left fw-bold text-dark mb-3">{quizData.quizTitle}</h1>
                      <p className="lead mb-4">{quizData.quizSynopsis}</p>
                      
                      <div className="d-flex flex-wrap gap-3 mb-4">
                        <span className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2">
                          <i className="fas fa-question-circle me-2"></i>
                          {quizData.questions?.length || 0} Questions
                        </span>
                        <span className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2">
                          <i className="fas fa-clock me-2"></i>
                          {Math.floor(parseFloat(localStorage.getItem("duration")))} Minutes
                        </span>
                        <span className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2">
                          <i className="fas fa-star me-2"></i>
                          Point Based
                        </span>
                      </div>
                    </div>
                    
                    <div className="col-md-4 text-center">
                      <div className="bg-primary bg-opacity-10 rounded-3 p-4 border">
                        <div className="text-primary fw-bold fs-2">
                          {Math.floor(parseFloat(localStorage.getItem("duration")))}
                        </div>
                        <div className="text-muted">Minutes Total</div>
                      </div>
                    </div>
                  </div>

                  {/* Important Instructions */}
                  <div className="alert alert-warning border-warning mb-5">
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-exclamation-triangle text-warning me-2 fs-4"></i>
                      <h4 className="h5 fw-bold mb-0 text-dark">Important Security Notice</h4>
                    </div>
                    <p className="mb-3 text-dark">
                      <strong>This assessment is actively monitored.</strong> Any attempt to cheat, including but not limited to:
                    </p>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="fas fa-ban text-danger me-2"></i>
                        Using unauthorized resources or assistance
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-ban text-danger me-2"></i>
                        Opening other browser tabs or applications
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-ban text-danger me-2"></i>
                        Attempting to copy or share questions
                      </li>
                      <li className="mb-0">
                        <i className="fas fa-ban text-danger me-2"></i>
                        Any form of impersonation or collaboration
                      </li>
                    </ul>
                    <hr className="my-3" />
                    <p className="mb-0 fw-bold text-danger">
                      <i className="fas fa-shield-alt me-2"></i>
                      Violations will result in immediate account suspension and permanent banning.
                    </p>
                  </div>

                  {/* Assessment Guidelines */}
                  <div className="row mb-5">
                    <div className="col-md-6 mb-4">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-list-check text-primary me-2"></i>
                        Before You Start
                      </h5>
                      <div className="list-group list-group-flush">
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Ensure stable internet connection
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Close all other applications
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Choose a quiet, distraction-free environment
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Have your identification ready if required
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6 mb-4">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-rotate text-primary me-2"></i>
                        During Assessment
                      </h5>
                      <div className="list-group list-group-flush">
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-ban text-danger me-2"></i>
                          Do not refresh or close the browser
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-ban text-danger me-2"></i>
                          Do not switch to other tabs
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-ban text-danger me-2"></i>
                          Do not use any external resources
                        </div>
                        <div className="list-group-item border-0 px-0 py-2">
                          <i className="fas fa-ban text-danger me-2"></i>
                          Do not take screenshots or photos
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Technical Requirements */}
                  <div className="bg-light rounded-3 p-4 mb-5">
                    <h5 className="fw-bold text-dark mb-3">
                      <i className="fas fa-laptop-code text-primary me-2"></i>
                      Technical Requirements
                    </h5>
                    <div className="row">
                      <div className="col-sm-6 mb-2">
                        <i className="fas fa-desktop text-primary me-2"></i>
                        Modern web browser (Chrome, Firefox, Safari, Edge)
                      </div>
                      <div className="col-sm-6 mb-2">
                        <i className="fas fa-wifi text-primary me-2"></i>
                        Stable internet connection (minimum 2 Mbps)
                      </div>
                      <div className="col-sm-6 mb-2">
                        <i className="fas fa-mobile-alt text-primary me-2"></i>
                        JavaScript enabled
                      </div>
                      <div className="col-sm-6 mb-2">
                        <i className="fas fa-camera text-primary me-2"></i>
                        Webcam may be required for proctoring
                      </div>
                    </div>
                  </div>

                  {/* Start Button */}
                  <div className="text-center">
                    <div className="d-grid gap-3">
                      <button 
                        onClick={startQuiz}
                        className="btn btn-primary btn-lg py-3 fw-bold fs-5 shadow"
                      >
                        <i className="fas fa-play-circle me-3"></i>
                        Start Assessment Now
                      </button>
                      <small className="text-dark">
                        By clicking "Start Assessment", you agree to abide by all assessment rules and guidelines
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Quiz
            onComplete={onQuizComplete}
            shuffle={true}
            showDefaultResult={false}
            quiz={quizData}
          />
        )}
      </div>

      <LoadingOverlay
        active={isActive}
        spinner
        text='Updating your score ...'
      ></LoadingOverlay>
    </div>
  );
};

export default QuizContainer;